import { AppConstants } from '@unpispas/upp-defs';
import { GenericUtils } from '@unpispas/upp-defs';
import { CountryCodes } from '@unpispas/upp-defs';
import { TaxIdValidators } from '@unpispas/upp-defs';
import { storeService } from '@unpispas/upp-base';
import { adhocService } from '@unpispas/upp-base';
import { configService } from '@unpispas/upp-base';
import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";

import { TicketChange } from './ticketchange';
import { AccountInvoice } from './accountinvoice';

import { Ticket } from './ticket';

import type { TicketDataRegistry } from './ticketdata';
import type { TicketDataTaxEntry } from './ticketdata';
import type { TicketView } from '../views/ticket';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { TicketChangeType } from '../types';
import { AccountInvoiceType } from '../types';

const _schema = {
    "name": "TICKETBAI",
    "fields": [
        { name: "tckchg", alias: "change", type: "objid" },
        { name: "invceac", alias: "invoice", type: "objid" },
        { name: "id", type: "string" },
        { name: "qr", type: "string" },
        { name: "xml", type: "string" },
        { name: "sign", type: "string" },
        { name: "error", type: "string" },
        { name: "sent", type: "date" },
        { name: "valid", type: "string" }
    ],
    relate: [
        { direction: ">", target: "TICKETCHANGE", by: "change", name: "change", reason: "change", child: false, class: TicketChangeType },
        { direction: ">", target: "ACCOUNTINVOICE", by: "invceac", name: "invoice", reason: "invoice", child: false, class: AccountInvoiceType },
    ]
} as const;

const _TicketbaiClass = CreateObject(_schema);
if (!_TicketbaiClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* TYPES DECLARATION        */
/****************************/

/** Scope values accepted by each regional TicketBAI endpoint. */
type TicketbaiRegion = 'Bizkaia' | 'Gipuzkoa' | 'Araba';
/** Supported TicketBAI document operations. */
type TicketbaiOperation = 'insert' | 'delete';

/** Aggregated VAT information at invoice level. */
type TicketBaiTaxLine = {
    rate: number | null;
    base: number | null;
    amount: number | null;
};

/** Commercial line representation used in TicketBAI payloads. */
type TicketBaiLine = {
    description: string;
    quantity: number;
    unitAmount: number | null;
    discountAmount: number | null;
    totalAmount: number | null;
};

/** Minimal invoice reference used for rectifications/cancellations. */
type TicketBaiInvoiceRef = {
    series: string | null;
    number: string | null;
    issuedAt: string | null;
    base?: number | null;
    taxes?: number | null;
};

type TicketBaiSoftwareInfo = {
    license: string;
    developerTaxId: string;
    name: string;
    version: string;
};

type TicketBaiRecipient = {
    name: string;
    taxId: string;
    country: string | null;
};

type TicketBaiRecipientIdOtro = {
    codigoPais: string;
    idType: '02' | '03' | '04' | '05' | '06';
    id: string;
};

type TicketBaiIssuer = {
    taxId: string | null;
    name: string | null;
};

type TicketBaiInvoice = {
    series: string | null;
    number: string | null;
    issuedAt: string | null;
    description: string;
    simplified: boolean;
    total: number | null;
    taxLines: TicketBaiTaxLine[];
    lines: TicketBaiLine[];
};

type TicketBaiRectification = {
    code: string;
    type: string;
    previous: TicketBaiInvoiceRef;
    substituted?: TicketBaiInvoiceRef[];
};

type TicketBaiCancellation = {
    target: TicketBaiInvoiceRef;
};

type TicketBaiPrevious = {
    series: string | null;
    number: string | null;
    issuedAt: string | null;
    signature: string | null;
};

type TicketBaiDataBase = {
    operation: TicketbaiOperation;
    region: TicketbaiRegion;
    issuer: TicketBaiIssuer;
    emittedBy: 'N' | 'T' | 'D';
    software: TicketBaiSoftwareInfo | null;
    previousChain: TicketBaiPrevious | null;
    changeReason: string | null;
};

type TicketBaiInvoiceData = TicketBaiDataBase & {
    operation: 'insert';
    recipient: TicketBaiRecipient | null;
    invoice: TicketBaiInvoice;
    rectification: TicketBaiRectification | null;
};

type TicketBaiCancellationData = TicketBaiDataBase & {
    operation: 'delete';
    cancellation: TicketBaiCancellation;
};

type TicketBaiData = TicketBaiInvoiceData | TicketBaiCancellationData;

/****************************/
/* SIGNING DEFINITIONS      */
/****************************/

type TicketBaiAuthorization = {
    privateKey: CryptoKey;      // Non-extractable RSASSA-PKCS1-v1_5 private key, ready to sign.
    certificatePem: string;     // Leaf certificate in PEM format (public, not secret).
};

type TicketBaiKdfConfig = {
    algorithm: string;
    iterations: number;
    keyLength: number;
    salt?: string;
};

type TicketBaiWrappedKey = {
    v: number;
    cipher: string;
    kdf: TicketBaiKdfConfig;
    iv: string;
    tag: string;
    payload: string;    // AES-GCM ciphertext of the raw PKCS#8 DER bytes, unwrapped directly into a CryptoKey.
};

type TicketBaiAuthorizeResponse = {
    certificatePem: string;
    certificateFingerprintSha256?: string;
    wrappedKey: TicketBaiWrappedKey;
};

type TicketBaiPayload = {
    xml: string;
    signature: string | null;
};

type TicketBaiChainStore = {
    created: string | null;
    series: string | null;
    invoice: string | null;
    signature: string | null;
};

type TicketBaiPolicy = {
    identifier: { value: string; };
    qualifiers?: string[];
    digestValue: string;
    hash: string;
};

/****************************/
/* TYPE HELPERS             */
/****************************/

/** Resolves the TicketBAI region from a fiscal province name. */
function ticketbaiRegion(province: string): TicketbaiRegion | null {
    const _regions: Array<[TicketbaiRegion, RegExp[]]> = [
        ['Bizkaia', [/Bizkaia/i, /Vizcaya/i]],
        ['Gipuzkoa', [/Guipúzcoa/i, /Guipuzcoa/i, /Gipuzkoa/i]],
        ['Araba', [/Álava/i, /Alava/i, /Araba/i]],
    ];

    for (const [_region, _patterns] of _regions){
        if (_patterns.some((pattern) => pattern.test(province))){
            return _region;
        }
    }

    return null;
}

/** Returns whether recipient can be represented as NIF in TicketBAI XML. */
function ticketBaiRecipientUsesNif(recipient: TicketBaiRecipient): boolean {
    if (recipient.country && !CountryCodes.Is_ES(recipient.country)){
        return false;
    }

    return TaxIdValidators.ValidateTaxId(recipient.taxId);
}

/** Builds IDOtro payload when recipient cannot be represented as NIF. */
function ticketBaiRecipientIdOtro(recipient: TicketBaiRecipient): TicketBaiRecipientIdOtro | null {
    if (ticketBaiRecipientUsesNif(recipient)){
        return null;
    }

    const _country = recipient.country;
    if (!_country){
        return {
            codigoPais: '',
            idType: '06',
            id: recipient.taxId.substring(0, 20),
        };
    }

    const _idType: TicketBaiRecipientIdOtro['idType'] = CountryCodes.Is_EU(_country) ? '02' : '04';
    let _id = recipient.taxId;
    if (_id.length > 20){
        _id = _id.substring(0, 20);
    }

    return {
        codigoPais: _country,
        idType: _idType,
        id: _id,
    };
}

/** Returns the chain link when complete; otherwise warns and omits the previous fingerprint. */
function ticketBaiResolvedPreviousChain(candidate: TicketBaiPrevious | null): TicketBaiPrevious | null {
    if (!candidate){
        return null;
    }

    const _missing: string[] = [];
    if ((candidate.number == null) || (candidate.number === '')){
        _missing.push('number');
    }
    if ((candidate.issuedAt == null) || (candidate.issuedAt === '')){
        _missing.push('issuedAt');
    }
    if ((candidate.signature == null) || (candidate.signature === '')){
        _missing.push('signature');
    }

    if (_missing.length > 0){
        console.warn('[TICKETBAI] Previous chain link malformed (' + _missing.join(', ') + ')');
        return null;
    }

    return candidate;
}

type TaxBreakdownRow = {
    rate: number;
    base: number;
    amount: number;
    total?: number;
};

/**
 * Drops VAT rows whose rounded base and amount are both zero when a real row remains.
 * Keeps one row when every row is zero so the breakdown is never empty.
 */
function TaxBreakdown(taxes: TaxBreakdownRow[] | null | undefined): TaxBreakdownRow[] {
    const _source = Array.isArray(taxes) ? taxes : [];
    const _normalized = _source.map((tax) => ({
        rate: Number(tax.rate) || 0,
        base: Number(tax.base) || 0,
        amount: Number(tax.amount) || 0,
        ...(tax.total != null ? { total: Number(tax.total) } : {}),
    }));

    if (_normalized.length === 0){
        return [];
    }

    const _real = _normalized.filter((tax) => {
        const _base = Math.round(tax.base * 100) / 100;
        const _amount = Math.round(tax.amount * 100) / 100;
        return _base !== 0 || _amount !== 0;
    });
    if (_real.length > 0){
        return _real;
    }

    return [_normalized[0]];
}

/** Pre-2025 VAT rates that TicketBAI still accepts only on corrective invoices. */
const TICKETBAI_OBSOLETE_RATES = [2, 5, 7.5];

/**
 * Strips whitespace from a SignatureValue and optionally truncates it.
 * Chain fingerprints keep the first 100 characters; the QR identifier keeps 13.
 */
function ticketBaiNormalizeSignature(
    value: string | null | undefined,
    maxLength: number | null = null
): string | null {
    if (value == null){
        return null;
    }

    const _clean = String(value).replace(/\s+/g, '');
    if (_clean.length === 0){
        return null;
    }

    if (maxLength == null || maxLength <= 0){
        return _clean;
    }

    return _clean.substring(0, maxLength);
}

/** Logs when a positive base still uses a VAT rate withdrawn on 01-01-2025. */
function ticketBaiWarnObsoleteRates(taxes: TaxBreakdownRow[]): void {
    for (const tax of taxes){
        const _rate = Number(tax.rate) || 0;
        if (TICKETBAI_OBSOLETE_RATES.includes(_rate) && (Number(tax.base) || 0) > 0){
            console.warn(
                '[TBAI] Tipo IVA ' + _rate + '% obsoleto desde 01-01-2025 — AEAT solo lo acepta en rectificativas.'
            );
        }
    }
}

const _ticketBaiPolicy: Record<TicketbaiRegion, TicketBaiPolicy> = {
    Bizkaia: {
        identifier: { value: 'https://www.batuz.eus/fitxategiak/batuz/ticketbai/sinadura_elektronikoaren_zehaztapenak_especificaciones_de_la_firma_electronica_v1_0.pdf' },
        qualifiers: [ 'https://www.batuz.eus/fitxategiak/batuz/ticketbai/sinadura_elektronikoaren_zehaztapenak_especificaciones_de_la_firma_electronica_v1_0.pdf' ],
        digestValue: 'Quzn98x3PMbSHwbUzaj5f5KOpiH0u8bvmwbbbNkO9Es=',
        hash: 'SHA-256'
    },
    Gipuzkoa: {
        identifier: { value: 'https://www.gipuzkoa.eus/ticketbai/sinadura' },
        digestValue: 'vSe1CH7eAFVkGN0X2Y7Nl9XGUoBnziDA5BGUSsyt8mg=',
        hash: 'SHA-256'
    },
    Araba: {
        identifier: { value: 'https://ticketbai.araba.eus/tbai/sinadura/' },
        digestValue: 'N5Uga0JQJ6kNBcXRUTKvRQz3UfJZTI/KrhS7ybSUNRo=',
        hash: 'SHA-256'
    }
};

/** Regional public endpoints that render the printable TicketBAI QR code. */
const _ticketBaiQrUrl: Record<TicketbaiRegion, { sbox: string; prod: string }> = {
    Bizkaia: {
        sbox: 'https://batuz.eus/QRTBAI/',
        prod: 'https://batuz.eus/QRTBAI/'
    },
    Gipuzkoa: {
        sbox: 'https://tbai.prep.gipuzkoa.eus/qr/',
        prod: 'https://tbai.egoitza.gipuzkoa.eus/qr/'
    },
    Araba: {
        sbox: 'https://pruebas-ticketbai.araba.eus/tbai/qrtbai/',
        prod: 'https://ticketbai.araba.eus/tbai/qrtbai'
    }
};

/****************************/
/* CLASS DECLARATION        */
/****************************/

/** Session-scoped signing modules (shared by all TicketBai XML builders). */
let _sessionXmldsigjs: any | null = null;
let _sessionXadesjs: any | null = null;
let _sessionLibrariesReady = false;
let _sessionLibrariesLoading: Promise<void> | null = null;
let _preloadTimer: ReturnType<typeof setTimeout> | null = null;

/** Delay before starting the background import so place UI chunks can settle first. */
const PRELOAD_DELAY_MS = 10000;

/**
 * Non-extractable signing key resolved per data session. Keyed by dataService (the
 * app's single data-layer instance) so the cache never leaks across sessions and is
 * never persisted — a CryptoKey cannot survive JSON/sessionStorage serialization.
 */
const _authorizationCache = new WeakMap<dataService, TicketBaiAuthorization>();

/**
 * Lazily loads xmldsigjs/xadesjs once per session.
 * Concurrent callers share the same in-flight promise.
 */
async function LoadTicketBaiLibraries(): Promise<void> {
    if (_sessionLibrariesReady){
        return;
    }

    if (_sessionLibrariesLoading){
        return _sessionLibrariesLoading;
    }

    _sessionLibrariesLoading = (async () => {
        if (!_sessionXmldsigjs){
            _sessionXmldsigjs = await import('xmldsigjs');
        }

        if (!_sessionXadesjs){
            _sessionXadesjs = await import('xadesjs');
        }

        _sessionLibrariesReady = !!(_sessionXmldsigjs && _sessionXadesjs);
    })();

    try {
        await _sessionLibrariesLoading;
    }
    catch (_error) {
        _sessionLibrariesLoading = null;
        throw _error;
    }
}

export class Ticketbai extends _TicketbaiClass { 
    /****************************/
    /* PUBLIC API               */
    /****************************/

    /** Whether xmldsigjs/xadesjs are already loaded for this browser session. */
    static get LibrariesReady(): boolean {
        return _sessionLibrariesReady;
    }

    /**
     * Schedules a best-effort preload of signing libraries when TicketBAI is enabled
     * and online. Starts after 10 seconds so place UI can load first; failures are
     * ignored — payment gate still protects offline signing.
     */
    static PreloadLibraries(place: { SendBAIEnabled: boolean }, online: boolean): void {
        if (_preloadTimer){
            clearTimeout(_preloadTimer);
            _preloadTimer = null;
        }

        if (!place.SendBAIEnabled || !online || _sessionLibrariesReady){
            return;
        }

        _preloadTimer = setTimeout(() => {
            _preloadTimer = null;
            if (_sessionLibrariesReady){
                return;
            }

            void LoadTicketBaiLibraries().catch(() => {
                // ignored — best-effort warmup; gate blocks payment if still missing offline
            });
        }, PRELOAD_DELAY_MS);
    }

    /** Instantiates a TicketBAI record bound to the shared data service. */
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    /** Creates a detached copy preserving object graph references. */
    Copy(store: Array<DataObject> = []): Ticketbai {
        return this._Copy(store) as Ticketbai;
    }

    /** Changelog rows lack parent fiscal lists; register on link (see TicketProduct). */
    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf){
            return;
        }

        if (relation === 'change' && related instanceof TicketChange){
            if (related === this.change){
                related.AddTicketbai(this);
            }
            else {
                related.DelTicketbai(this);
            }
            return;
        }

        // relate.by is the FK column (`invceac`), not the alias (`invoice`).
        if (relation === 'invceac' && related instanceof AccountInvoice){
            if (related === this.invoice){
                related.AddTicketbai(this);
            }
            else {
                related.DelTicketbai(this);
            }
        }
    }

    /**
     * Rebuilds TicketBAI payload from current registry and stores signed XML/signature.
     */
    async Refresh(): Promise<void> {
        const _store = this.data.injector.get(storeService);

        if (this.change){
            const _change = this.change;
            const _cachedChain = this.ReadCachedChain(_change, null);
            await this._RefreshFromChange(_store, _change, _cachedChain);
            return;
        }

        if (this.invoice){
            const _invoice = this.invoice;
            const _cachedChain = this.ReadCachedChain(null, _invoice);
            await this._RefreshFromInvoice(_store, _invoice, _cachedChain);
            return;
        }

        console.error('[TICKETBAI] No ticket change or account invoice provided.');
    }

    /** Indicates whether current TicketBAI payload has been validated by backend. */
    get IsValid(): boolean {
        return (this.valid != null);
    }

    /****************************/
    /* PRIVATE: REFRESH FLOW    */
    /****************************/

    /** Rebuilds TicketBAI payload from a ticket change registry snapshot. */
    private async _RefreshFromChange(
        store: storeService,
        change: TicketChange,
        cachedChain: TicketBaiPrevious | null
    ): Promise<void> {
        const _registry = change.ticket?.ticketdata?.Registry;
        if (!_registry){
            console.error('[TICKETBAI] Invalid or missing ticketdata.registry.');
            return;
        }

        const _calc = _TicketBaiChangeCalc.Create(_registry, store, cachedChain, this.data, change.ticket);
        if (!_calc){
            console.error('[TICKETBAI] Registry is not suitable for TicketBAI calculation.');
            return;
        }

        await this._ApplyCalcResult(_calc, change, null);
    }

    /** Rebuilds TicketBAI payload from a consolidated account invoice. */
    private async _RefreshFromInvoice(
        store: storeService,
        invoice: AccountInvoice,
        cachedChain: TicketBaiPrevious | null
    ): Promise<void> {
        if (!invoice.invoice){
            console.error('[TICKETBAI] Account invoice number is missing.');
            return;
        }

        const _calc = _TicketBaiInvoiceCalc.Create(invoice, store, cachedChain, this.data);
        if (!_calc){
            console.error('[TICKETBAI] Account invoice is not suitable for TicketBAI calculation.');
            return;
        }

        await this._ApplyCalcResult(_calc, null, invoice);
    }

    /** Builds signed XML, fiscal QR fields, and persists the chain fingerprint. */
    private async _ApplyCalcResult(
        calc: _TicketBaiChangeCalc | _TicketBaiInvoiceCalc,
        change: TicketChange | null,
        invoice: AccountInvoice | null
    ): Promise<void> {
        const _data = await calc.Data();
        if (!_data){
            console.error('[TICKETBAI] Registry is not suitable for TicketBAI calculation.');
            return;
        }

        const _xml = new _TicketBaiXml(_data, this.data);
        const _payload = await _xml.Build();
        if (!_payload){
            console.error('[TICKETBAI] Could not generate signed TicketBAI XML.');
            return;
        }
        this.xml = _payload.xml;
        this.sign = _payload.signature;

        const _fiscalqr = calc.FiscalQr(_payload.signature);
        this.id = _fiscalqr.id;
        this.qr = _fiscalqr.qr;

        this.StoreCachedChain(change, invoice);
    }

    /****************************/
    /* PRIVATE: CACHE HELPERS   */
    /****************************/

    /** Resolves the place identifier from the linked change or invoice. */
    private _placeid(change: TicketChange | null, invoice: AccountInvoice | null): string {
        if (change){
            return String(change.ticket?.place?.objid ?? null);
        }

        if (invoice){
            return String(invoice.place?.objid ?? null);
        }

        throw new Error('[TICKETBAI] No ticket change or account invoice provided.');
    }

    /** Reads the last signed TicketBAI chain entry from device ticket storage. */
    private ReadCachedChain(change: TicketChange | null, invoice: AccountInvoice | null): TicketBaiPrevious | null {
        const _placeid = this._placeid(change, invoice);

        const _cfg = this.data.injector.get(configService);
        const _ticketstore = _cfg.getConfiguration(_placeid + '_DEVICE_TICKETS');
        const _chain = (_ticketstore.get('tbai') ?? null) as TicketBaiChainStore | null;
        if (!_chain?.signature){
            return null;
        }

        return {
            series: _chain.series || null,
            number: _chain.invoice || null,
            issuedAt: _chain.created || null,
            signature: ticketBaiNormalizeSignature(_chain.signature, 100)
        };
    }

    /** Persists the current signature as the next chain link for the place device. */
    private StoreCachedChain(change: TicketChange | null, invoice: AccountInvoice | null): void {
        const _signature = ticketBaiNormalizeSignature(this.sign, 100);
        if (!_signature){
            return;
        }

        const _placeid = this._placeid(change, invoice);

        const _cfg = this.data.injector.get(configService);
        const _ticketstore = _cfg.getConfiguration(_placeid + '_DEVICE_TICKETS');
        if (change){
            _ticketstore.set('tbai', {
                created: change.created?.toISOString() || null,
                series: change.series || null,
                invoice: change.invoice || null,
                signature: _signature,
            } as TicketBaiChainStore);
            return;
        }

        if (invoice){
            _ticketstore.set('tbai', {
                created: invoice.created?.toISOString() || null,
                series: invoice.series || null,
                invoice: invoice.invoice || null,
                signature: _signature,
            } as TicketBaiChainStore);
        }
    }

}

/*******************************/
/* TICKETBAI CHANGE CALC       */
/*******************************/

class _TicketBaiChangeCalc {
    /*******************************/
    /* PUBLIC FACTORY              */
    /*******************************/

    /** Validates registry prerequisites and returns a calculation context. */
    static Create(
        registry: TicketDataRegistry | null,
        store: storeService | null,
        baichain: TicketBaiPrevious | null,
        data: dataService,
        ticket: Ticket | null
    ): _TicketBaiChangeCalc | null {
        if (!registry){
            return null;
        }

        const _TicketRef = registry.ticketRef;
        if (_TicketRef){
            if ((!_TicketRef.series) || (!_TicketRef.invceno)){
                return null;    // invoice number is required
            }
        }
        else {
            return null;    // no ticket reference
        }

        const _region = ticketbaiRegion(registry.fiscal?.region || '');
        if (!_region){
            return null;    // no suitable region
        }

        const _operation = this.TicketbaiOperation(registry);
        if (!_operation){
            return null;    // no operation
        }
        
        if (!store?.SecureReady()){
            console.error('[TICKETBAI] Crypto environment is not available.');
            return null;    // crypto environment is not capable
        }
        
        return new _TicketBaiChangeCalc(_operation, _region, registry, baichain, data, ticket);
    }

    /*******************************/
    /* PRIVATE: FACTORY HELPERS    */
    /*******************************/

    /** Returns whether the registry represents a cancellation (DELETE state). */
    private static IsErrorneous(registry: TicketDataRegistry): boolean {
        return registry.commercial?.state === 'DELETE';
    }

    /** Maps registry commercial state to the TicketBAI document operation. */
    private static TicketbaiOperation(registry: TicketDataRegistry): TicketbaiOperation | null {
        if (this.IsErrorneous(registry)){
            return 'delete';
        }

        return 'insert';
    }

    /*******************************/
    /* CONSTRUCTOR & ENTRYPOINT    */
    /*******************************/

    /** Private constructor; use {@link _TicketBaiChangeCalc.Create} for validated instances. */
    private constructor(
        private operation: TicketbaiOperation,
        private region: TicketbaiRegion,
        private registry: TicketDataRegistry,
        private baichain: TicketBaiPrevious | null,
        private data: dataService,
        private ticket: Ticket | null
    ){
        // Keep the constructor private: callers must pass through Create() validation.
    }

    /** Economic 0% on this ticket may be emitted only for an eligible recipient NIF. */
    private _BlocksZeroRate(taxId: string | null | undefined, country: string | null | undefined): boolean {
        const _view = this.ticket?.View as TicketView | null;
        if (!_view?.ZeroRateInvoice){
            return false;
        }

        return !TaxIdValidators.IsZeroRate(taxId, country);
    }

    /** Builds the normalized TicketBAI domain payload from registry snapshot. */
    async Data(): Promise<TicketBaiData | null> {
        const _issuer = this.Issuer();
        const _emittedBy = this.EmittedBy();
        const _software = this.SoftwareInfo();
        const _previousChain = this.PreviousChain();
        const _base = {
            operation: this.operation,
            region: this.region,
            issuer: _issuer,
            emittedBy: _emittedBy,
            software: _software,
            previousChain: _previousChain,
            changeReason: this.CurrentChange()?.reason || null
        } as const;
        const _data: TicketBaiData = (this.operation === 'delete')
            ? {
                ..._base,
                operation: 'delete',
                cancellation: this.CancellationTarget()
            }
            : {
                ..._base,
                operation: 'insert',
                recipient: this.Recipient(),
                invoice: this.Invoice(),
                rectification: this.Rectification()
            };

        if (!this._CheckData(_data)){
            return null;
        }

        return _data;
    }

    /*******************************/
    /* PUBLIC: FISCAL QR           */
    /*******************************/

    /**
     * Builds the printable TicketBAI identifier and QR URL from the signed
     * invoice signature. Only emission ('insert') documents carry a QR code.
     */
    FiscalQr(signatureValue: string | null): { id: string | null; qr: string | null } {
        if (this.operation !== 'insert'){
            return { id: null, qr: null };
        }

        const _id = this.QrIdentifier(signatureValue);
        if (!_id){
            return { id: null, qr: null };
        }

        return { id: _id, qr: this.QrUrl(_id) };
    }

    /*******************************/
    /* PRIVATE: FISCAL QR HELPERS  */
    /*******************************/

    /** Builds the TBAI-prefixed identifier embedded in the printable QR code. */
    private QrIdentifier(signatureValue: string | null): string | null {
        const _taxId = String(this.registry.merchant?.taxId || '').trim();
        const _date = this.QrIdentifierDate();
        const _sign = ticketBaiNormalizeSignature(signatureValue, 13) || '';
        if (!_taxId || !_date || (_sign.length === 0)){
            return null;
        }

        // TBAI-<NIF>-<DDMMYY>-<13 first chars of SignatureValue>-<CRC8>
        const _prefix = 'TBAI-' + _taxId + '-' + _date + '-' + _sign + '-';
        return _prefix + GenericUtils.CRC8(_prefix);
    }

    /** Formats the invoice issue date as DDMMYY for the QR identifier segment. */
    private QrIdentifierDate(): string | null {
        // Must match FechaExpedicionFactura (ISO issue date) used in the signed XML.
        const _issuedAt = String(this.IssuedAt() || '').trim();
        if (_issuedAt.length < 10){
            return null;
        }

        const _dd = _issuedAt.substring(8, 10);
        const _mm = _issuedAt.substring(5, 7);
        const _yy = _issuedAt.substring(2, 4);
        return _dd + _mm + _yy;
    }

    /** Builds the regional TicketBAI QR verification URL for the given identifier. */
    private QrUrl(identifier: string): string | null {
        const _endpoint = _ticketBaiQrUrl[this.region];
        if (!_endpoint){
            return null;
        }

        const _base = AppConstants.TicketBaiSandbox ? _endpoint.sbox : _endpoint.prod;
        const _series = String(this.registry.ticketRef?.series || '').trim();
        const _invoice = String(this.registry.ticketRef?.invceno || '').trim();
        const _total = (this._round(this.registry.commercial?.totals?.total) ?? 0).toFixed(2);

        // <base>/?id=<id>&s=<serie>&nf=<numfactura>&i=<importe>&cr=<CRC8>
        let _qr = _base + '/';
        _qr += '?id=' + encodeURIComponent(identifier);
        _qr += '&s=' + _series;
        _qr += '&nf=' + _invoice;
        _qr += '&i=' + _total;
        _qr += '&cr=' + GenericUtils.CRC8(_qr);
        return _qr;
    }

    /*******************************/
    /* PRIVATE: DATA CALCULATION   */
    /*******************************/

    /** Rounds a monetary value to two decimal places. */
    private _round(value: number | null | undefined): number | null {
        if (value == null){
            return null;
        }

        return Math.round(value * 100) / 100;
    }

    /** Extracts issuer tax ID and name from the registry merchant block. */
    private Issuer(): TicketBaiData['issuer'] {
        return {
            taxId: this.registry.merchant?.taxId || null,
            name: this.registry.merchant?.name || null
        };
    }

    /** Returns the TicketBAI emission channel code for this flow. */
    private EmittedBy(): 'N' | 'T' | 'D' {        
        return 'T'; // TicketBAI files are generated/signed by a third-party operator in our flow.
    }

    /** Builds the commercial invoice block from registry ticket and totals data. */
    private Invoice(): TicketBaiInvoice {
        const _recipient = this.Recipient();
        const _isDeleted = this.IsCancelled();

        return {
            series: this.registry.ticketRef?.series || null,
            number: this.registry.ticketRef?.invceno || null,
            issuedAt: this.IssuedAt(),
            description: this.InvoiceDescription(),
            simplified: _recipient === null,
            total: _isDeleted ? 0 : this._round(this.registry.commercial?.totals?.total),
            taxLines: this.TaxLines(),
            lines: this.InvoiceLines()
        };
    }

    /** Builds rectification metadata when the change amends a previous invoice. */
    private Rectification(): TicketBaiRectification | null {
        const _previous = this.PreviousInvoiceRef();
        const _reason = this.CurrentChange()?.reason || '';
        const _isDeleted = this.IsCancelled();
        if (!_previous || (!['P', 'I', 'D'].includes(_reason) && !_isDeleted)){
            return null;
        }

        return {
            code: this.Recipient() ? 'R1' : 'R5',
            type: 'S',
            previous: _previous
        };
    }

    /** Builds the cancellation target reference for delete operations. */
    private CancellationTarget(): TicketBaiCancellation {
        const _previous = this.PreviousInvoiceRef();
        return {
            target: _previous || {
                series: this.registry.ticketRef?.series || null,
                number: this.registry.ticketRef?.invceno || null,
                issuedAt: this.IssuedAt()
            }
        };
    }

    /** Resolves the invoice issue timestamp from fiscal data or the current change. */
    private IssuedAt(): string | null {
        return this.registry.fiscal?.issuedAt || this.CurrentChange()?.date || null;
    }

    /** Derives the human-readable invoice description from change reason or merchant. */
    private InvoiceDescription(): string {
        if (this.IsCancelled()){
            return 'Rectificacion por cancelacion';
        }

        const _reason = this.CurrentChange()?.reason || '';
        switch (_reason){
            case 'I':
                return 'Emisión de factura';
            case 'D':
                return 'Cambios en destinatario';
            case 'P':
                return 'Cambios en importe';
            default: {
                const _merchant = this.registry.merchant?.name || '';
                return _merchant ? ('Ticket ' + _merchant) : 'Ticket';
            }
        }
    }

    /** Builds the immediately preceding invoice reference from registry changes. */
    private PreviousInvoiceRef(): TicketBaiInvoiceRef | null {
        const _previous = this.PreviousChange();
        if (!_previous){
            return null;
        }

        return {
            series: _previous.series || null,
            number: _previous.invoice || null,
            issuedAt: _previous.date || null,
            base: this._round(_previous.base),
            taxes: this._round(_previous.taxes)
        };
    }

    /**
     * Resolves EncadenamientoFacturaAnterior from the device chain cache only.
     * Previous change of the same ticket is for rectification refs, not the SIF chain
     * (Gipuzkoa FAQ: last invoice of the device regardless of series).
     */
    private PreviousChain(): TicketBaiPrevious | null {
        const _chain = this.baichain;
        if (!_chain?.signature){
            return null;
        }

        const _currentSeries = this.registry.ticketRef?.series || null;
        const _currentInvoice = this.registry.ticketRef?.invceno || null;
        if ((_chain.series === _currentSeries) && (_chain.number === _currentInvoice)){
            return null;
        }

        return ticketBaiResolvedPreviousChain({
            series: _chain.series || null,
            number: _chain.number || null,
            issuedAt: _chain.issuedAt || null,
            signature: ticketBaiNormalizeSignature(_chain.signature, 100)
        });
    }

    /** Returns the most recent change entry in the registry snapshot. */
    private CurrentChange(): TicketDataRegistry['changes'][number] | null {
        const _changes = this.registry.changes || [];
        return (_changes.length > 0) ? _changes[_changes.length -1] : null;
    }

    /** Returns the change entry immediately before the current one, if any. */
    private PreviousChange(): TicketDataRegistry['changes'][number] | null {
        const _changes = this.registry.changes || [];
        return (_changes.length > 1) ? _changes[_changes.length -2] : null;
    }

    /** Maps registry recipient data to the TicketBAI recipient block, or null if simplified. */
    private Recipient(): TicketBaiRecipient | null {
        const _recipient = this.registry.recipient;
        if (!_recipient){
            return null;
        }

        const _name = String(_recipient.name || '').trim();
        const _taxId = String(_recipient.taxId || '').trim();
        if (!_name || !_taxId){
            return null;
        }

        return {
            name: _name,
            taxId: _taxId,
            country: _recipient.country,
        };
    }

    /** Builds commercial detail lines from products, extras, and discounts. */
    private InvoiceLines(): TicketBaiLine[] {
        if (this.IsCancelled()){
            return [
                {
                    description: 'Rectificacion por cancelacion',
                    quantity: 1,
                    unitAmount: 0,
                    discountAmount: null,
                    totalAmount: 0
                }
            ];
        }

        const _lines: TicketBaiLine[] = [];

        for (const _line of (this.registry.commercial?.lines || [])){
            const _quantity = Number(_line.qty || 1);
            const _amounts = _line.amounts || {};
            const _options = (_line.options || [])
                .map((option) => option.name?.label || '')
                .filter((name) => !!name);
            const _description = [_line.name?.label || 'Producto', ..._options].join(' - ');
            const _base = this._round(_amounts.base);
            _lines.push({
                description: _description,
                quantity: _quantity,
                unitAmount: this._round((_quantity !== 0 && _base != null) ? (_base / _quantity) : _base),
                discountAmount: null,
                totalAmount: this._round(_amounts.total)
            });
        }

        for (const _extra of (this.registry.commercial?.extras || [])){
            _lines.push({
                description: _extra.name?.label || 'Extra',
                quantity: 1,
                unitAmount: this._round(_extra.amounts?.base),
                discountAmount: null,
                totalAmount: this._round(_extra.amounts?.total)
            });
        }

        for (const _discount of (this.registry.commercial?.discounts || [])){
            _lines.push({
                description: _discount.name?.label || 'Descuento',
                quantity: 1,
                unitAmount: this._round(_discount.amounts?.base),
                discountAmount: null,
                totalAmount: this._round(_discount.amounts?.total)
            });
        }

        return _lines;
    }

    /** Aggregates VAT breakdown lines from commercial totals. */
    private TaxLines(): TicketBaiTaxLine[] {
        if (this.IsCancelled()){
            return [
                {
                    rate: 0,
                    base: 0,
                    amount: 0
                }
            ];
        }

        const _taxes = this.registry.commercial?.totals?.taxes || [];
        if (_taxes.length === 0){
            return [];
        }

        const _sanitized = TaxBreakdown(_taxes);
        ticketBaiWarnObsoleteRates(_sanitized);

        return _sanitized.map((tax) => ({
            rate: this._round(tax.rate),
            base: this._round(tax.base),
            amount: this._round(tax.amount)
        }));
    }

    /** Returns whether the ticket commercial state is cancelled or deleted. */
    private IsCancelled(): boolean {
        return ['CANCEL', 'DELETE'].includes(this.registry.commercial?.state || '');
    }

    /** Resolves regional TicketBAI software metadata from application constants. */
    private SoftwareInfo(): TicketBaiSoftwareInfo | null {
        try {
            return {
                license: AppConstants.TicketBaiLicense(this.region),
                developerTaxId: AppConstants.TicketBaiNIF(this.region),
                name: AppConstants.TicketBaiAppname(this.region),
                version: AppConstants.TicketBaiAppvers(this.region)
            };
        }
        catch {
            return null;
        }
    }

    /*******************************/
    /* PRIVATE: DATA VALIDATION    */
    /*******************************/

    /** Validates assembled payload fields and logs missing required keys. */
    private _CheckData(data: TicketBaiData): boolean {
        const _errors: string[] = [];

        this.Require(_errors, 'issuer.taxId', data.issuer.taxId);
        this.Require(_errors, 'issuer.name', data.issuer.name);
        this.Require(_errors, 'software.license', data.software?.license);
        this.Require(_errors, 'software.developerTaxId', data.software?.developerTaxId);
        this.Require(_errors, 'software.name', data.software?.name);
        this.Require(_errors, 'software.version', data.software?.version);

        if (data.operation === 'insert'){
            this.Require(_errors, 'invoice.number', data.invoice.number);
            this.Require(_errors, 'invoice.issuedAt', data.invoice.issuedAt);
            this.Require(_errors, 'invoice.description', data.invoice.description);
            this.Require(_errors, 'invoice.total', data.invoice.total);

            if (!data.invoice.simplified){
                this.Require(_errors, 'recipient.taxId', data.recipient?.taxId);
                this.Require(_errors, 'recipient.name', data.recipient?.name);
                if (data.recipient && !ticketBaiRecipientUsesNif(data.recipient)){
                    const _idOtro = ticketBaiRecipientIdOtro(data.recipient);
                    this.Require(_errors, 'recipient.idOtro.id', _idOtro?.id);
                    this.Require(_errors, 'recipient.idOtro.idType', _idOtro?.idType);
                }
            }

            if (data.invoice.lines.length === 0){
                _errors.push('invoice.lines');
            }
            if (data.invoice.taxLines.length === 0){
                _errors.push('invoice.taxLines');
            }
            if (this._BlocksZeroRate(data.recipient?.taxId, data.recipient?.country)){
                _errors.push('invoice.taxLines.zeroRate');
            }

            if (data.rectification){
                this.Require(_errors, 'rectification.previous.number', data.rectification.previous.number);
                this.Require(_errors, 'rectification.previous.issuedAt', data.rectification.previous.issuedAt);
                this.Require(_errors, 'rectification.previous.base', data.rectification.previous.base);
                this.Require(_errors, 'rectification.previous.taxes', data.rectification.previous.taxes);
            }

            if (data.previousChain){
                this.Require(_errors, 'previousChain.number', data.previousChain.number);
                this.Require(_errors, 'previousChain.issuedAt', data.previousChain.issuedAt);
                this.Require(_errors, 'previousChain.signature', data.previousChain.signature);
            }
        }
        else {
            this.Require(_errors, 'cancellation.target.number', data.cancellation.target.number);
            this.Require(_errors, 'cancellation.target.issuedAt', data.cancellation.target.issuedAt);
        }

        if (_errors.length > 0){
            for (const _error of _errors){
                console.error('[TICKETBAI] Validation error: ' + _error);
            }
            return false;
        }

        return true;
    }

    /** Appends a validation key to the error list when the value is empty. */
    private Require(errors: string[], key: string, value: any): void {
        if ((value == null) || (value === '')){
            errors.push(key);
        }
    }
}

/*******************************/
/* TICKETBAI INVOICE CALC      */
/*******************************/

class _TicketBaiInvoiceCalc {
    /*******************************/
    /* PUBLIC FACTORY              */
    /*******************************/

    /** Validates account invoice prerequisites and returns a calculation context. */
    static Create(
        invoice: AccountInvoice,
        store: storeService | null,
        baichain: TicketBaiPrevious | null,
        data: dataService
    ): _TicketBaiInvoiceCalc | null {
        const _place = invoice.place;
        if (!_place){
            return null;
        }

        const _region = ticketbaiRegion(_place.fiscal?.province || '');
        if (!_region){
            return null;
        }

        if (!invoice.series || !invoice.invoice){
            return null;
        }

        if (invoice.tickets.length === 0){
            return null;
        }

        if (!store?.SecureReady()){
            console.error('[TICKETBAI] Crypto environment is not available.');
            return null;
        }

        return new _TicketBaiInvoiceCalc(_region, invoice, baichain, data);
    }

    /*******************************/
    /* CONSTRUCTOR & ENTRYPOINT    */
    /*******************************/

    /** Private constructor; use {@link _TicketBaiInvoiceCalc.Create} for validated instances. */
    private constructor(
        private region: TicketbaiRegion,
        private invoice: AccountInvoice,
        private baichain: TicketBaiPrevious | null,
        private data: dataService
    ){
        // Keep the constructor private: callers must pass through Create() validation.
    }

    /** Economic 0% on any linked ticket may be emitted only for an eligible recipient NIF. */
    private _BlocksZeroRate(taxId: string | null | undefined, country: string | null | undefined): boolean {
        const _zero = this.invoice.tickets.some((_ticket) => {
            const _view = _ticket.View as TicketView | null;
            return _view?.ZeroRateInvoice === true;
        });
        if (!_zero){
            return false;
        }

        return !TaxIdValidators.IsZeroRate(taxId, country);
    }

    /** Builds the normalized TicketBAI domain payload from the account invoice. */
    async Data(): Promise<TicketBaiInvoiceData | null> {
        const _data: TicketBaiInvoiceData = {
            operation: 'insert',
            region: this.region,
            issuer: this.Issuer(),
            emittedBy: 'T',
            software: this.SoftwareInfo(),
            previousChain: this.PreviousChain(),
            changeReason: 'A',
            recipient: this.Recipient(),
            invoice: this.Invoice(),
            rectification: this.Rectification(),
        };

        if (!this._CheckData(_data)){
            return null;
        }

        return _data;
    }

    /*******************************/
    /* PUBLIC: FISCAL QR           */
    /*******************************/

    /** Builds the printable TicketBAI identifier and QR URL from the signed signature. */
    FiscalQr(signatureValue: string | null): { id: string | null; qr: string | null } {
        const _id = this.QrIdentifier(signatureValue);
        if (!_id){
            return { id: null, qr: null };
        }

        return { id: _id, qr: this.QrUrl(_id) };
    }

    /*******************************/
    /* PRIVATE: FISCAL QR HELPERS  */
    /*******************************/

    /** Builds the TBAI-prefixed identifier embedded in the printable QR code. */
    private QrIdentifier(signatureValue: string | null): string | null {
        const _taxId = String(this.invoice.place?.taxid || '').trim();
        const _date = this.QrIdentifierDate();
        const _sign = String(signatureValue || '').trim().slice(0, 13);
        if (!_taxId || !_date || (_sign.length === 0)){
            return null;
        }

        const _prefix = 'TBAI-' + _taxId + '-' + _date + '-' + _sign + '-';
        return _prefix + GenericUtils.CRC8(_prefix);
    }

    /** Formats the invoice issue date as DDMMYY for the QR identifier segment. */
    private QrIdentifierDate(): string | null {
        const _issuedAt = String(this.IssuedAt() || '').trim();
        if (_issuedAt.length < 10){
            return null;
        }

        const _dd = _issuedAt.substring(8, 10);
        const _mm = _issuedAt.substring(5, 7);
        const _yy = _issuedAt.substring(2, 4);
        return _dd + _mm + _yy;
    }

    /** Builds the regional TicketBAI QR verification URL for the given identifier. */
    private QrUrl(identifier: string): string | null {
        const _endpoint = _ticketBaiQrUrl[this.region];
        if (!_endpoint){
            return null;
        }

        const _base = AppConstants.TicketBaiSandbox ? _endpoint.sbox : _endpoint.prod;
        const _series = String(this.invoice.series || '').trim();
        const _invoiceNo = String(this.invoice.invoice || '').trim();
        const _total = (this._round(this.invoice.total) ?? 0).toFixed(2);

        let _qr = _base + '/';
        _qr += '?id=' + encodeURIComponent(identifier);
        _qr += '&s=' + _series;
        _qr += '&nf=' + _invoiceNo;
        _qr += '&i=' + _total;
        _qr += '&cr=' + GenericUtils.CRC8(_qr);
        return _qr;
    }

    /*******************************/
    /* PRIVATE: DATA CALCULATION   */
    /*******************************/

    /** Rounds a monetary value to two decimal places. */
    private _round(value: number | null | undefined): number | null {
        if (value == null){
            return null;
        }

        return Math.round(value * 100) / 100;
    }

    /** Extracts issuer tax ID and business name from the invoice place. */
    private Issuer(): TicketBaiIssuer {
        const _place = this.invoice.place;
        return {
            taxId: _place?.taxid || null,
            name: _place?.business || null,
        };
    }

    /** Maps account client data to the TicketBAI recipient block, or null if simplified. */
    private Recipient(): TicketBaiRecipient | null {
        const _client = this.invoice.account?.client ?? null;
        if (!_client){
            return null;
        }

        const _name = String(_client.name || '').trim();
        const _taxId = String(_client.taxid || '').trim();
        if (!_name || !_taxId){
            return null;
        }

        return {
            name: _name,
            taxId: _taxId,
            country: _client.country ?? null,
        };
    }

    /** Builds the consolidated invoice block from linked tickets and tax split. */
    private Invoice(): TicketBaiInvoice {
        const _recipient = this.Recipient();
        const _split = this.invoice.SplitTaxes.price.tax.split;
        const _lines: TicketBaiLine[] = [];

        for (const _ticket of this.invoice.tickets){
            let _lineBase = 0;
            for (const _tax of this._TicketTaxes(_ticket)){
                _lineBase += _tax.base;
            }

            _lines.push({
                description: String(_ticket.series || '') + String(_ticket.invceno || ''),
                quantity: 1,
                unitAmount: this._round(_lineBase),
                discountAmount: null,
                totalAmount: this._round(_ticket.price),
            });
        }

        const _sanitized = TaxBreakdown(
            _split.map((_tax) => ({
                rate: Number(_tax.rate || 0),
                base: Number(_tax.base || 0),
                amount: Number(_tax.taxes || 0),
                total: Number(_tax.total || 0),
            }))
        );
        ticketBaiWarnObsoleteRates(_sanitized);

        return {
            series: this.invoice.series || null,
            number: this.invoice.invoice || null,
            issuedAt: this.IssuedAt(),
            description: 'Factura recapitulativa',
            simplified: _recipient === null,
            total: this._round(this.invoice.total),
            taxLines: _sanitized.map((_tax) => ({
                rate: this._round(_tax.rate),
                base: this._round(_tax.base),
                amount: this._round(_tax.amount),
            })),
            lines: _lines,
        };
    }

    /** Builds substitutive rectification metadata for the recap invoice tickets. */
    private Rectification(): TicketBaiRectification {
        const _client = this.invoice.account?.client ?? null;
        let _totalBase = 0;
        let _totalTaxes = 0;
        const _substituted: TicketBaiInvoiceRef[] = [];

        for (const _ticket of this.invoice.tickets){
            for (const _tax of this._TicketTaxes(_ticket)){
                _totalBase += _tax.base;
                _totalTaxes += _tax.amount;
            }

            _substituted.push({
                series: _ticket.series || null,
                number: _ticket.invceno || null,
                issuedAt: this._TicketIssuedAt(_ticket),
            });
        }

        return {
            code: _client ? 'R4' : 'R5',
            type: 'S',
            previous: {
                series: null,
                number: null,
                issuedAt: null,
                base: this._round(_totalBase),
                taxes: this._round(_totalTaxes),
            },
            substituted: _substituted,
        };
    }

    /** Resolves the signature chain link from cached device storage. */
    private PreviousChain(): TicketBaiPrevious | null {
        const _chain = this.baichain;
        if (!_chain?.signature){
            return null;
        }

        const _currentSeries = this.invoice.series || null;
        const _currentInvoice = this.invoice.invoice || null;
        if ((_chain.series === _currentSeries) && (_chain.number === _currentInvoice)){
            return null;
        }

        return ticketBaiResolvedPreviousChain({
            series: _chain.series || null,
            number: _chain.number || null,
            issuedAt: _chain.issuedAt || null,
            signature: ticketBaiNormalizeSignature(_chain.signature, 100),
        });
    }

    /** Returns the account invoice issue timestamp. */
    private IssuedAt(): string | null {
        return this._InvoiceIssuedAt();
    }

    /** Normalizes recap invoice issue time from the model; null when not stamped. */
    private _InvoiceIssuedAt(): string | null {
        const _created = this.invoice.created;
        if (_created instanceof Date){
            return _created.toISOString();
        }

        return null;
    }

    /** Normalizes a linked ticket issue time, falling back to the recap invoice date. */
    private _TicketIssuedAt(ticket: Ticket): string | null {
        const _created = ticket.created;
        if (_created instanceof Date){
            return _created.toISOString();
        }

        return this._InvoiceIssuedAt();
    }

    /** Returns sanitized tax entries from a ticket registry commercial totals. */
    private _TicketTaxes(ticket: Ticket): TicketDataTaxEntry[] {
        const _registry = ticket.ticketdata?.Registry;
        const _taxes = _registry?.commercial?.totals?.taxes || [];
        const _sanitized = TaxBreakdown(_taxes);

        return _sanitized
            .filter((_tax) => (_tax.total ?? ((_tax.base || 0) + (_tax.amount || 0))) !== 0)
            .map((_tax) => ({
                rate: _tax.rate,
                base: _tax.base,
                amount: _tax.amount,
                total: _tax.total ?? ((_tax.base || 0) + (_tax.amount || 0)),
            }));
    }

    /** Resolves regional TicketBAI software metadata from application constants. */
    private SoftwareInfo(): TicketBaiSoftwareInfo | null {
        try {
            return {
                license: AppConstants.TicketBaiLicense(this.region),
                developerTaxId: AppConstants.TicketBaiNIF(this.region),
                name: AppConstants.TicketBaiAppname(this.region),
                version: AppConstants.TicketBaiAppvers(this.region),
            };
        }
        catch {
            return null;
        }
    }

    /*******************************/
    /* PRIVATE: DATA VALIDATION    */
    /*******************************/

    /** Validates assembled payload fields and logs missing required keys. */
    private _CheckData(data: TicketBaiInvoiceData): boolean {
        const _errors: string[] = [];

        this.Require(_errors, 'issuer.taxId', data.issuer.taxId);
        this.Require(_errors, 'issuer.name', data.issuer.name);
        this.Require(_errors, 'software.license', data.software?.license);
        this.Require(_errors, 'software.developerTaxId', data.software?.developerTaxId);
        this.Require(_errors, 'software.name', data.software?.name);
        this.Require(_errors, 'software.version', data.software?.version);
        this.Require(_errors, 'invoice.number', data.invoice.number);
        this.Require(_errors, 'invoice.issuedAt', data.invoice.issuedAt);
        this.Require(_errors, 'invoice.description', data.invoice.description);
        this.Require(_errors, 'invoice.total', data.invoice.total);

        if (!data.invoice.simplified){
            this.Require(_errors, 'recipient.taxId', data.recipient?.taxId);
            this.Require(_errors, 'recipient.name', data.recipient?.name);
            if (data.recipient && !ticketBaiRecipientUsesNif(data.recipient)){
                const _idOtro = ticketBaiRecipientIdOtro(data.recipient);
                this.Require(_errors, 'recipient.idOtro.id', _idOtro?.id);
                this.Require(_errors, 'recipient.idOtro.idType', _idOtro?.idType);
            }
        }

        if (data.invoice.lines.length === 0){
            _errors.push('invoice.lines');
        }
        if (data.invoice.taxLines.length === 0){
            _errors.push('invoice.taxLines');
        }
        if (this._BlocksZeroRate(data.recipient?.taxId, data.recipient?.country)){
            _errors.push('invoice.taxLines.zeroRate');
        }

        if (data.rectification){
            if (data.rectification.substituted?.length){
                for (let _index = 0; _index < data.rectification.substituted.length; _index++){
                    const _substituted = data.rectification.substituted[_index];
                    this.Require(_errors, 'rectification.substituted[' + _index + '].number', _substituted.number);
                    this.Require(_errors, 'rectification.substituted[' + _index + '].issuedAt', _substituted.issuedAt);
                }
            }
            else {
                this.Require(_errors, 'rectification.previous.number', data.rectification.previous.number);
                this.Require(_errors, 'rectification.previous.issuedAt', data.rectification.previous.issuedAt);
            }
            this.Require(_errors, 'rectification.previous.base', data.rectification.previous.base);
            this.Require(_errors, 'rectification.previous.taxes', data.rectification.previous.taxes);
        }

        if (data.previousChain){
            this.Require(_errors, 'previousChain.number', data.previousChain.number);
            this.Require(_errors, 'previousChain.issuedAt', data.previousChain.issuedAt);
            this.Require(_errors, 'previousChain.signature', data.previousChain.signature);
        }

        if (_errors.length > 0){
            for (const _error of _errors){
                console.error('[TICKETBAI] Validation error: ' + _error);
            }
            return false;
        }

        return true;
    }

    /** Appends a validation key to the error list when the value is empty. */
    private Require(errors: string[], key: string, value: any): void {
        if ((value == null) || (value === '')){
            errors.push(key);
        }
    }
}

/*******************************/
/* TICKETBAI XML               */
/*******************************/

class _TicketBaiXml {
    /*******************************/
    /* STATE & CONSTRUCTOR         */
    /*******************************/

    /** Creates an XML builder bound to a normalized TicketBAI domain payload. */
    constructor(private data: TicketBaiData, private dataService: dataService){
        // nothing to do
    }

    /*******************************/
    /* PRIVATE: SERVICE ACCESSORS  */
    /*******************************/

    /** Resolves the adhoc backend request service from the data injector. */
    private get adhoc(): adhocService {
        return this.dataService.injector.get(adhocService);
    }

    /*******************************/
    /* PRIVATE: DYNAMIC LIBRARIES  */
    /*******************************/

    /** Lazily loads xmldsigjs and xadesjs modules required for XAdES signing. */
    private async LoadLibraries(): Promise<void> {
        await LoadTicketBaiLibraries();
    }

    /*******************************/
    /* PUBLIC BUILD FLOW           */
    /*******************************/

    /** Builds and signs the final TicketBAI XML payload. */
    async Build(): Promise<TicketBaiPayload | null> {
        await this.LoadLibraries();

        const _unsignedXml = (this.data.operation === 'delete')
            ? this.CancelXml(this.data)
            : this.InvoiceXml(this.data);
        
        return await this.TrySignXml(_unsignedXml);
    }

    /*******************************/
    /* PRIVATE: XML BUILDERS       */
    /*******************************/

    /** Serializes an invoice emission document to unsigned TicketBAI XML. */
    private InvoiceXml(data: TicketBaiInvoiceData): string {
        return '<?xml version="1.0" encoding="UTF-8"?>' +
            '<T:TicketBai xmlns:T="urn:ticketbai:emision" xmlns:ds="http://www.w3.org/2000/09/xmldsig#">' +
                '<Cabecera><IDVersionTBAI>1.2</IDVersionTBAI></Cabecera>' +
                '<Sujetos>' +
                    '<Emisor>' +
                        this.Node('NIF', data.issuer.taxId) +
                        this.Node('ApellidosNombreRazonSocial', data.issuer.name) +
                    '</Emisor>' +
                    this.RecipientXml(data) +
                    this.Node('EmitidaPorTercerosODestinatario', data.emittedBy) +
                '</Sujetos>' +
                '<Factura>' +
                    '<CabeceraFactura>' +
                        this.OptionalNode('SerieFactura', data.invoice.series) +
                        this.Node('NumFactura', data.invoice.number) +
                        this.Node('FechaExpedicionFactura', this.DateOnly(data.invoice.issuedAt)) +
                        this.Node('HoraExpedicionFactura', this.TimeOnly(data.invoice.issuedAt)) +
                        this.OptionalNode('FacturaSimplificada', data.invoice.simplified ? 'S' : 'N') +
                        this.Node(
                            'FacturaEmitidaSustitucionSimplificada',
                            data.changeReason === 'I' ? 'S' : 'N'
                        ) +
                        this.RectificationXml(data) +
                    '</CabeceraFactura>' +
                    '<DatosFactura>' +
                        this.Node('DescripcionFactura', data.invoice.description) +
                        '<DetallesFactura>' + this.LinesXml(data) + '</DetallesFactura>' +
                        this.Node('ImporteTotalFactura', this.Amount(data.invoice.total)) +
                        '<Claves><IDClave><ClaveRegimenIvaOpTrascendencia>01</ClaveRegimenIvaOpTrascendencia></IDClave></Claves>' +
                    '</DatosFactura>' +
                    this.SubjectBreakdownXml(data) +
                '</Factura>' +
                this.FingerprintXml(data) +
            '</T:TicketBai>';
    }

    /**
     * Builds TipoDesglose as Sujeta/NoExenta/S1. A 0% rate stays TipoImpositivo 0
     * when the recipient NIF is eligible; otherwise validation rejects the payload.
     */
    private SubjectBreakdownXml(data: TicketBaiInvoiceData): string {
        return '<TipoDesglose><DesgloseFactura><Sujeta><NoExenta><DetalleNoExenta>' +
            '<TipoNoExenta>S1</TipoNoExenta>' +
            '<DesgloseIVA>' + this.TaxesXml(data) + '</DesgloseIVA>' +
        '</DetalleNoExenta></NoExenta></Sujeta></DesgloseFactura></TipoDesglose>';
    }

    /** Serializes a cancellation document to unsigned AnulaTicketBai XML. */
    private CancelXml(data: TicketBaiCancellationData): string {
        const _target = data.cancellation.target;
        return '<?xml version="1.0" encoding="UTF-8"?>' +
            '<T:AnulaTicketBai xmlns:T="urn:ticketbai:anulacion" xmlns:ds="http://www.w3.org/2000/09/xmldsig#">' +
                '<Cabecera><IDVersionTBAI>1.2</IDVersionTBAI></Cabecera>' +
                '<IDFactura>' +
                    '<Emisor>' +
                        this.Node('NIF', data.issuer.taxId) +
                        this.Node('ApellidosNombreRazonSocial', data.issuer.name) +
                    '</Emisor>' +
                    '<CabeceraFactura>' +
                        this.OptionalNode('SerieFactura', _target.series) +
                        this.Node('NumFactura', _target.number) +
                        this.Node('FechaExpedicionFactura', this.DateOnly(_target.issuedAt)) +
                    '</CabeceraFactura>' +
                '</IDFactura>' +
                this.FingerprintXml(data) +
            '</T:AnulaTicketBai>';
    }

    /** Builds rectification and substituted-invoice XML fragments when present. */
    private RectificationXml(data: TicketBaiInvoiceData): string {
        if (!data.rectification){
            return '';
        }

        const _previous = data.rectification.previous;
        const _substituted = data.rectification.substituted?.length ? data.rectification.substituted : [ data.rectification.previous ];

        return '<FacturaRectificativa>' +
                this.Node('Codigo', data.rectification.code) +
                this.Node('Tipo', data.rectification.type) +
                '<ImporteRectificacionSustitutiva>' +
                    this.Node('BaseRectificada', this.Amount(_previous.base ?? 0)) +
                    this.Node('CuotaRectificada', this.Amount(_previous.taxes ?? 0)) +
                '</ImporteRectificacionSustitutiva>' +
            '</FacturaRectificativa>' +
            '<FacturasRectificadasSustituidas>' +
                _substituted.map((_item) =>
                    '<IDFacturaRectificadaSustituida>' +
                        this.OptionalNode('SerieFactura', _item.series) +
                        this.Node('NumFactura', _item.number) +
                        this.Node('FechaExpedicionFactura', this.DateOnly(_item.issuedAt)) +
                    '</IDFacturaRectificadaSustituida>'
                ).join('') +
            '</FacturasRectificadasSustituidas>';
    }

    /** Builds the Destinatarios block for full (non-simplified) invoices. */
    private RecipientXml(data: TicketBaiInvoiceData): string {
        if (data.invoice.simplified || data.recipient === null){
            return '';
        }

        return '<Destinatarios>' +
            '<IDDestinatario>' +
                this.RecipientIdentificationXml(data.recipient) +
                this.Node('ApellidosNombreRazonSocial', data.recipient.name) +
            '</IDDestinatario>' +
        '</Destinatarios>';
    }

    /** Builds NIF or IDOtro identification XML for a recipient. */
    private RecipientIdentificationXml(recipient: TicketBaiRecipient): string {
        if (ticketBaiRecipientUsesNif(recipient)){
            return this.Node('NIF', recipient.taxId);
        }

        const _idOtro = ticketBaiRecipientIdOtro(recipient);
        if (!_idOtro){
            return '';
        }

        let _xml = '<IDOtro>';
        if (_idOtro.codigoPais){
            _xml += this.Node('CodigoPais', _idOtro.codigoPais);
        }
        _xml += this.Node('IDType', _idOtro.idType);
        _xml += this.Node('ID', _idOtro.id);
        _xml += '</IDOtro>';
        return _xml;
    }

    /** Serializes all commercial detail lines for the invoice DatosFactura block. */
    private LinesXml(data: TicketBaiInvoiceData): string {
        return data.invoice.lines.map((line) => (
            this.LineXml(line.description, line.quantity, line.unitAmount, line.discountAmount, line.totalAmount)
        )).join('');
    }

    /** Serializes a single IDDetalleFactura line with amounts and optional discount. */
    private LineXml(description: string, qty: number, unit: number | null, discount: number | null, total: number | null): string {
        return '<IDDetalleFactura>' +
            this.Node('DescripcionDetalle', description) +
            this.Node('Cantidad', String(qty)) +
            this.Node('ImporteUnitario', this.Amount(unit)) +
            this.OptionalNode('Descuento', this.AmountOrNull(discount)) +
            this.Node('ImporteTotal', this.Amount(total)) +
        '</IDDetalleFactura>';
    }

    /** Serializes VAT breakdown DetalleIVA nodes for the invoice. */
    private TaxesXml(data: TicketBaiInvoiceData): string {
        return data.invoice.taxLines.map((tax) => (
            '<DetalleIVA>' +
                this.Node('BaseImponible', this.Amount(tax.base)) +
                this.OptionalNode('TipoImpositivo', this.AmountOrNull(tax.rate)) +
                this.OptionalNode('CuotaImpuesto', this.AmountOrNull(tax.amount)) +
            '</DetalleIVA>'
        )).join('');
    }

    /** Builds the HuellaTBAI fingerprint block with chain link and software metadata. */
    private FingerprintXml(data: TicketBaiData): string {
        const _previousChain = (data.operation === 'insert') ? data.previousChain : null;
        const _previous = _previousChain
            ? '<EncadenamientoFacturaAnterior>' +
                this.OptionalNode('SerieFacturaAnterior', _previousChain.series) +
                this.Node('NumFacturaAnterior', _previousChain.number) +
                this.Node('FechaExpedicionFacturaAnterior', this.DateOnly(_previousChain.issuedAt)) +
                this.Node('SignatureValueFirmaFacturaAnterior', _previousChain.signature) +
            '</EncadenamientoFacturaAnterior>'
            : '';

        return '<HuellaTBAI>' +
            _previous +
            '<Software>' +
                this.Node('LicenciaTBAI', data.software?.license ?? null) +
                '<EntidadDesarrolladora>' + this.Node('NIF', data.software?.developerTaxId ?? null) + '</EntidadDesarrolladora>' +
                this.Node('Nombre', data.software?.name ?? null) +
                this.Node('Version', data.software?.version ?? null) +
            '</Software>' +
        '</HuellaTBAI>';
    }

    /*******************************/
    /* PRIVATE: AUTHORIZATION      */
    /*******************************/

    /** Resolves signing certificate/key from the in-memory session cache or backend endpoints. */
    private async ResolveAuthorization(): Promise<TicketBaiAuthorization | null> {
        const _doDerivedUrl = 'ticketbai/derived';
        const _doAuthorizeUrl = 'ticketbai/authorize';

        const _cached = _authorizationCache.get(this.dataService);
        if (_cached){
            return _cached;
        }

        let _material: TicketBaiAuthorization | null = null;
        try {
            let _pin = '';

            const _derived_data = await this.adhoc.DoRequest(_doDerivedUrl);
            if (_derived_data){
                const _success = (_derived_data['errorcode'] === 0)
                if (!_success){
                    console.error('[TICKETBAI] ' + _doDerivedUrl  + ' request returned errorcode: [' + _derived_data['errorcode']+ ']');
                    return null;
                }

                _pin = String(_derived_data['pin'] || '').trim();
            } 

            const _data: any = await this.adhoc.DoRequest(_doAuthorizeUrl);
            if (_data){
                const _success = (_data['errorcode'] === 0)
                if (!_success){
                    console.error('[TICKETBAI] ' + _doAuthorizeUrl  + ' request returned errorcode: [' + _data['errorcode']+ ']');
                    return null;
                }

                _material = await this.UnwrapAuthorization(_data, _pin);
            }

            if (!_material?.privateKey || !_material?.certificatePem){
                console.error('[TICKETBAI] Authorization is incomplete.');
                return null;
            }
        }
        catch (_error) {
            console.error('[TICKETBAI] Authorization request failed:', _error);
            return null;
        }

        _authorizationCache.set(this.dataService, _material);
        return _material;
    }

    /** Unwraps the backend-issued private key directly into a non-extractable CryptoKey using the session PIN and AES-GCM. */
    private async UnwrapAuthorization(data: unknown, pin: string): Promise<TicketBaiAuthorization | null> {
        if (!data || typeof data !== 'object'){
            console.error('[TICKETBAI] Authorization response is missing.');
            return null;
        }

        const _data = data as TicketBaiAuthorizeResponse;
        const _certificatePem = String(_data?.certificatePem || '');
        const _wrapped = (_data?.wrappedKey && typeof _data.wrappedKey === 'object') ? _data.wrappedKey : null;
        if (!_certificatePem || !_wrapped){
            console.error('[TICKETBAI] Wrapped key material is missing.');
            return null;
        }

        const _cipher = String(_wrapped?.cipher || '');
        const _kdf = (_wrapped?.kdf && typeof _wrapped.kdf === 'object') ? _wrapped.kdf : null;
        const _kdfAlgorithm = String(_kdf?.algorithm || '').toUpperCase();
        const _kdfIterations = Number(_kdf?.iterations || 0);
        const _kdfKeyLength = Number(_kdf?.keyLength || 32);
        const _kdfSalt = String(_kdf?.salt || '');
        const _ivB64 = String(_wrapped?.iv || '');
        const _tagB64 = String(_wrapped?.tag || '');
        const _payloadB64 = String(_wrapped?.payload || '');

        if (_cipher !== 'AES-256-GCM'){
            console.error('[TICKETBAI] Unsupported wrapped key cipher:', _cipher);
            return null;
        }
        if (_kdfAlgorithm !== 'PBKDF2-SHA256'){
            console.error('[TICKETBAI] Unsupported KDF algorithm:', _kdfAlgorithm);
            return null;
        }
        if (!_kdfSalt || _kdfIterations <= 0 || _kdfKeyLength <= 0){
            console.error('[TICKETBAI] Invalid KDF config on authorization response.');
            return null;
        }
        if (!_ivB64 || !_tagB64 || !_payloadB64){
            console.error('[TICKETBAI] Wrapped key payload is incomplete.');
            return null;
        }

        try {
            const _pinBytes = new TextEncoder().encode(pin);
            const _salt = GenericUtils.base64ToArrayBuffer(this.NormalizeBase64Input(_kdfSalt));
            const _iv = new Uint8Array(GenericUtils.base64ToArrayBuffer(this.NormalizeBase64Input(_ivB64)));
            const _tag = new Uint8Array(GenericUtils.base64ToArrayBuffer(this.NormalizeBase64Input(_tagB64)));
            const _ciphertext = new Uint8Array(GenericUtils.base64ToArrayBuffer(this.NormalizeBase64Input(_payloadB64)));

            const _wrappedKeyBytes = new Uint8Array(_ciphertext.length + _tag.length);
            _wrappedKeyBytes.set(_ciphertext, 0);
            _wrappedKeyBytes.set(_tag, _ciphertext.length);

            const _keyMaterial = await globalThis.crypto.subtle.importKey(
                'raw',
                _pinBytes,
                { name: 'PBKDF2' },
                false,
                ['deriveKey']
            );
            const _unwrappingKey = await globalThis.crypto.subtle.deriveKey(
                {
                    name: 'PBKDF2',
                    hash: 'SHA-256',
                    salt: _salt,
                    iterations: Math.floor(_kdfIterations)
                },
                _keyMaterial,
                {
                    name: 'AES-GCM',
                    length: Math.floor(_kdfKeyLength) * 8
                },
                false,
                ['unwrapKey']
            );

            const _privateKey = await globalThis.crypto.subtle.unwrapKey(
                'pkcs8',
                _wrappedKeyBytes,
                _unwrappingKey,
                {
                    name: 'AES-GCM',
                    iv: _iv,
                    tagLength: 128
                },
                {
                    name: 'RSASSA-PKCS1-v1_5',
                    hash: 'SHA-256'
                },
                false,
                ['sign']
            );

            return {
                privateKey: _privateKey,
                certificatePem: _certificatePem
            };
        }
        catch (_error) {
            console.error('[TICKETBAI] Could not unwrap authorization private key:', _error);
            return null;
        }
    }

    /*******************************/
    /* PRIVATE: XADES UTILITIES    */
    /*******************************/

    /** Signs XML using XAdES policy and extracts signature value for chaining. */
    private async TrySignXml(xml: string): Promise<TicketBaiPayload | null> {
        const _material = await this.ResolveAuthorization();
        if (!_material){
            return null;
        }

        try {
            const _xades = _sessionXadesjs?.default || _sessionXadesjs;
            if (!_xades){
                throw new Error('xadesjs module is not loaded.');
            }

            const _xmlDoc = _xades.Parse(xml);
            const _signedXml = new _xades.SignedXml();

            const _privateKey = _material.privateKey;
            const _leafCertB64 = this.NormalizeBase64Input(_material.certificatePem);
            const _policy = _ticketBaiPolicy[this.data.region];
            const _signOptions: any = {
                references: [{
                    hash: 'SHA-256',
                    uri: '',
                    transforms: ['enveloped', 'c14n']
                }],
                x509: [_leafCertB64],
                signingCertificate: _leafCertB64
            };
            if (_policy){
                _signOptions.policy = _policy;
            }

            await _signedXml.Sign(
                {
                    name: 'RSASSA-PKCS1-v1_5'
                },
                _privateKey,
                _xmlDoc,
                _signOptions
            );

            const _signedNode = _signedXml.XmlSignature.GetXml();
            if (!_signedNode){
                throw new Error('Signed XML node could not be generated.');
            }
            _xmlDoc.documentElement.appendChild(_xmlDoc.importNode(_signedNode, true));

            const _signed = new XMLSerializer().serializeToString(_xmlDoc);
            const _signature = this.ExtractSignatureValue(_signed);
            return {
                xml: _signed,
                signature: _signature
            };
        }
        catch (_error) {
            console.error('[TICKETBAI] Could not sign XML offline:', _error);
            return null;
        }
    }

    /** Strips PEM headers and whitespace from a base64-encoded key or certificate. */
    private NormalizeBase64Input(value: string): string {
        return value
            .replace(/-----BEGIN [^-]+-----/g, '')
            .replace(/-----END [^-]+-----/g, '')
            .replace(/\s+/g, '');
    }

    /** Reads the ds:SignatureValue text from a signed XML document (whitespace stripped). */
    private ExtractSignatureValue(xml: string): string | null {
        const _doc = new DOMParser().parseFromString(xml, 'application/xml');
        const _nodes = _doc.getElementsByTagNameNS('http://www.w3.org/2000/09/xmldsig#', 'SignatureValue');
        if (_nodes.length === 0){
            return null;
        }

        return ticketBaiNormalizeSignature(_nodes[0].textContent);
    }

    /*******************************/
    /* PRIVATE: XML UTILITIES      */
    /*******************************/

    /** Builds a required XML element with escaped text content. */
    private Node(name: string, value: string | number | null): string {
        return '<' + name + '>' + this.Escape(value ?? '') + '</' + name + '>';
    }

    /** Builds an XML element only when the value is non-empty. */
    private OptionalNode(name: string, value: string | number | null): string {
        return (value == null || value === '') ? '' : this.Node(name, value);
    }

    /** Formats an ISO timestamp as DD-MM-YYYY for TicketBAI date fields. */
    private DateOnly(value: string | null): string {
        return value ? value.substring(0, 10).split('-').reverse().join('-') : '';
    }

    /** Extracts HH:MM:SS from an ISO timestamp for TicketBAI time fields. */
    private TimeOnly(value: string | null): string {
        return value ? value.substring(11, 19) : '';
    }

    /** Formats a numeric amount for optional XML nodes, or returns null when empty. */
    private AmountOrNull(value: number | string | null): string | null {
        if (value == null || value === ''){
            return null;
        }
        return this.Amount(value);
    }

    /** Formats a numeric amount as a fixed two-decimal string for XML. */
    private Amount(value: number | string | null): string {
        const _number = Number(value);
        if (!Number.isFinite(_number)){
            return '0.00';
        }
        return _number.toFixed(2);
    }

    /** Escapes special XML characters in text node content. */
    private Escape(value: string | number): string {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
    }
}
